import { useId, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';
import { CustomerButton } from '@/components/customer-portal/CustomerButton';
import { CustomerFileUpload } from '@/components/customer-portal/CustomerFileUpload';

/**
 * D-129 — the customer's approval form. An approval is only submittable with
 * all three of: Work Order number, Work Order description, Work Order PDF.
 * The server enforces the same rule; this form just refuses to submit an
 * incomplete approval and explains what is missing.
 *
 * Premium UI pass: same validation, same payload, same accessible names and
 * test ids; the presentation moves to the shared customer form language
 * (labelled fields with helper text, a custom upload surface, a checklist of
 * what is still missing and a full-width primary CTA on phones).
 */

export interface CustomerWorkOrderApprovalPayload {
  workOrderNumber: string;
  workOrderDescription: string;
  file: File;
}

interface Props {
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (payload: CustomerWorkOrderApprovalPayload) => void;
}

const MAX_PDF_BYTES = 25 * 1024 * 1024;

export function isPdf(file: Pick<File, 'name' | 'type'>): boolean {
  if (file.type === 'application/pdf') return true;
  return file.type === '' && /\.pdf$/i.test(file.name);
}

const FIELD_CLASS =
  'mt-1.5 block w-full rounded-customer-sm border border-customer-border bg-customer-surface px-3.5 text-sm text-customer-text placeholder:text-customer-text-secondary transition-colors hover:border-customer-text/30 focus:border-customer-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring/40 disabled:cursor-not-allowed disabled:opacity-60';

export function CustomerWorkOrderApprovalForm({ submitting, onCancel, onSubmit }: Props) {
  const uid = useId();
  const [workOrderNumber, setWorkOrderNumber] = useState('');
  const [workOrderDescription, setWorkOrderDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const numberOk = workOrderNumber.trim().length > 0;
  const descriptionOk = workOrderDescription.trim().length > 0;
  const fileOk = !!file && !fileError;
  const complete = numberOk && descriptionOk && fileOk;

  function handleFile(next: File | null) {
    if (!next) {
      setFile(null);
      setFileError(null);
      return;
    }
    if (!isPdf(next)) {
      setFile(null);
      setFileError('Only PDF files are accepted for the Work Order.');
      return;
    }
    if (next.size > MAX_PDF_BYTES) {
      setFile(null);
      setFileError('The Work Order PDF must be 25 MB or smaller.');
      return;
    }
    setFile(next);
    setFileError(null);
  }

  const missing = [
    !numberOk && 'Work Order number',
    !descriptionOk && 'Work Order description',
    !fileOk && 'Work Order PDF',
  ].filter(Boolean) as string[];

  return (
    <form
      className="customer-slide-up mt-4 space-y-5 rounded-customer border border-customer-primary/25 bg-customer-primary/[0.04] p-4 sm:p-5"
      aria-label="Customer approval"
      onSubmit={(e) => {
        e.preventDefault();
        if (!complete || !file || submitting) return;
        onSubmit({
          workOrderNumber: workOrderNumber.trim(),
          workOrderDescription: workOrderDescription.trim(),
          file,
        });
      }}
    >
      <div>
        <p className="text-base font-semibold text-customer-text">Customer Approval</p>
        <p className="mt-1 text-sm leading-5 text-customer-text-secondary">
          To approve this quotation, provide your Work Order details and attach the Work Order PDF.
          All three are required.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor={`${uid}-wo-number`}
            className="block text-sm font-medium text-customer-text"
          >
            Work Order Number{' '}
            <span className="text-customer-danger-text" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id={`${uid}-wo-number`}
            type="text"
            value={workOrderNumber}
            onChange={(e) => setWorkOrderNumber(e.target.value)}
            maxLength={255}
            required
            aria-required="true"
            disabled={submitting}
            className={`${FIELD_CLASS} min-h-11`}
            placeholder="e.g. WO-2026-0042"
            autoComplete="off"
          />
          <p className="mt-1 text-xs text-customer-text-secondary">
            Your internal purchase or Work Order reference for this repair.
          </p>
        </div>

        <div>
          <label
            htmlFor={`${uid}-wo-description`}
            className="block text-sm font-medium text-customer-text"
          >
            Work Order Description{' '}
            <span className="text-customer-danger-text" aria-hidden="true">
              *
            </span>
          </label>
          <textarea
            id={`${uid}-wo-description`}
            value={workOrderDescription}
            onChange={(e) => setWorkOrderDescription(e.target.value)}
            rows={3}
            maxLength={5000}
            required
            aria-required="true"
            disabled={submitting}
            className={`${FIELD_CLASS} min-h-[6rem] resize-y py-2.5 leading-5`}
            placeholder="Describe the work you are authorising"
          />
          <p className="mt-1 text-xs text-customer-text-secondary">
            A short statement of the scope you are authorising, as written on your Work Order.
          </p>
        </div>

        <CustomerFileUpload
          id={`${uid}-wo-file`}
          label="Work Order PDF"
          required
          accept="application/pdf,.pdf"
          hint="Tap to attach your Work Order PDF"
          guidance="PDF only · up to 25 MB"
          file={file}
          error={fileError}
          disabled={submitting}
          onFileChange={handleFile}
          removeLabel="Remove Work Order PDF"
        />
      </div>

      <div
        className={`flex items-start gap-2 rounded-customer-sm px-3 py-2 text-xs ${
          complete
            ? 'bg-customer-success/10 text-customer-success-text'
            : 'bg-customer-surface text-customer-text-secondary'
        }`}
      >
        <FontAwesomeIcon
          icon={complete ? faCircleCheck : faCircleExclamation}
          className="mt-0.5 h-3.5 w-3.5 shrink-0"
          aria-hidden="true"
        />
        {complete ? (
          <span className="font-medium">
            Everything is in place — you can submit your approval.
          </span>
        ) : (
          <span data-testid="wo-missing">Missing: {missing.join(', ')}</span>
        )}
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <CustomerButton
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
          className="sm:min-w-[7rem]"
        >
          Cancel
        </CustomerButton>
        <CustomerButton
          type="submit"
          variant="primary"
          size="lg"
          icon={faPaperPlane}
          disabled={!complete || submitting}
          loading={submitting}
          loadingLabel="Submitting…"
          className="sm:min-w-[12rem]"
        >
          Approve &amp; Submit
        </CustomerButton>
      </div>
    </form>
  );
}
